import { type ReactNode, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { Button as TamaguiButton, XStack, YStack } from "tamagui";

import {
  AppCard,
  AppCardBody,
  AppContentContainer,
  AppHeroShell,
  AppPage,
  AppShell,
  AppShellMain,
  AppText,
  kindredFontFamilies,
} from "../../ui-foundation";
import { KindredLogoButton, MemberHeaderActions } from "@/components/MemberHeaderControls";
import {
  KINDRED_PRIVACY_DATA_RIGHTS_LABEL,
  STEADY_HOUSE_COMPANY_NUMBER,
  STEADY_HOUSE_LEGAL_NAME,
} from "../../shared/support";

type DocumentFooterLink = {
  href: string;
  label: string;
};

type DocumentPageScaffoldProps = {
  title: string;
  updatedLabel?: string;
  fallbackPath: string;
  footerLinks?: DocumentFooterLink[];
  footerNote?: string;
  children: ReactNode;
};

type DocumentSectionProps = {
  title: string;
  children: ReactNode;
};

type DocumentSubsectionProps = {
  title: string;
  children: ReactNode;
};

type DocumentListProps = {
  items: ReactNode[];
};

const defaultFooterLinks: DocumentFooterLink[] = [
  { href: "/about-kindred", label: "About Kindred" },
  { href: "/terms", label: "Terms & Conditions" },
  { href: "/privacy", label: "Privacy Policy" },
  { href: "/data-rights", label: KINDRED_PRIVACY_DATA_RIGHTS_LABEL },
  { href: "/conduct", label: "Community Guidelines" },
  { href: "/child-safety", label: "Child Safety" },
  { href: "/editorial-policy", label: "Editorial Policy" },
  { href: "/contact-complaints", label: "Contact & complaints" },
];

export function DocumentPageScaffold({
  title,
  updatedLabel,
  fallbackPath,
  footerLinks = defaultFooterLinks,
  footerNote = `© 2026 Kindred Community. Operated by ${STEADY_HOUSE_LEGAL_NAME} (company ${STEADY_HOUSE_COMPANY_NUMBER}).`,
  children,
}: DocumentPageScaffoldProps) {
  const navigate = useNavigate();

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "auto" });
  }, []);

  useEffect(() => {
    const previousTitle = document.title;
    document.title = `${title} | Kindred | Single parent community app`;

    return () => {
      document.title = previousTitle;
    };
  }, [title]);

  const handleBack = () => {
    if (window.history.length > 1) {
      navigate(-1);
      return;
    }

    navigate(fallbackPath);
  };

  return (
    <AppShell minHeight="100vh">
      <header className="relative z-20">
        <AppContentContainer pt="$xs">
          <AppHeroShell
            title={title}
            titleLines={2}
            leading={<KindredLogoButton />}
            backAction={
              <TamaguiButton
                unstyled
                type="button"
                width={40}
                height={40}
                alignItems="center"
                justifyContent="center"
                borderWidth={1}
                borderRadius="$pill"
                borderColor="$borderColor"
                backgroundColor="$cardBackground"
                pressStyle={{ opacity: 0.92 }}
                onPress={handleBack}
                aria-label="Go back"
              >
                <ArrowLeft size={18} className="text-secondary" />
              </TamaguiButton>
            }
            action={<MemberHeaderActions />}
          />
        </AppContentContainer>
      </header>

      <AppShellMain>
        <AppPage tag="main" gap="$md" maxWidth={960}>
          {updatedLabel ? (
            <AppText color="$colorMuted" fontSize="$sm" lineHeight="$sm">
              {updatedLabel}
            </AppText>
          ) : null}

          <YStack gap="$md">{children}</YStack>

          <AppCard>
            <AppCardBody alignItems="center" gap="$sm">
              <img
                src="/kindred-logo-full.png"
                alt="Kindred"
                style={{ display: "block", height: 24, width: "auto", opacity: 0.7 }}
              />
              <XStack flexWrap="wrap" justifyContent="center" gap="$sm">
                {footerLinks.map((link) =>
                  link.href.startsWith("/") ? (
                    <Link key={link.href} to={link.href} className="kindred-inline-link text-sm">
                      {link.label}
                    </Link>
                  ) : (
                    <a key={link.href} href={link.href} className="kindred-inline-link text-sm">
                      {link.label}
                    </a>
                  ),
                )}
              </XStack>
              <AppText color="$colorMuted" fontSize="$xs" lineHeight="$sm" textAlign="center">
                {footerNote}
              </AppText>
            </AppCardBody>
          </AppCard>
        </AppPage>
      </AppShellMain>
    </AppShell>
  );
}

export function DocumentSection({ title, children }: DocumentSectionProps) {
  return (
    <AppCard>
      <AppCardBody gap="$sm">
        <AppText fontFamily={kindredFontFamilies.display} fontSize="$xl" lineHeight="$xl" color="$heading">
          {title}
        </AppText>
        <YStack gap="$xs">{children}</YStack>
      </AppCardBody>
    </AppCard>
  );
}

export function DocumentSubsection({ title, children }: DocumentSubsectionProps) {
  return (
    <YStack gap={6}>
      <AppText fontFamily={kindredFontFamilies.sansBold} fontSize="$md" lineHeight="$md">
        {title}
      </AppText>
      <YStack gap={6}>{children}</YStack>
    </YStack>
  );
}

export function DocumentList({ items }: DocumentListProps) {
  return (
    <YStack gap={8}>
      {items.map((item, index) => (
        <XStack key={index} alignItems="flex-start" gap="$sm">
          <YStack
            width={8}
            height={8}
            marginTop={7}
            borderRadius="$pill"
            backgroundColor="$primary"
            flexShrink={0}
          />
          <AppText flex={1} color="$color" fontSize="$sm" lineHeight="$md">
            {item}
          </AppText>
        </XStack>
      ))}
    </YStack>
  );
}
